import React, { useMemo } from 'react'
import { SubmitErrorHandler, useFormContext } from 'react-hook-form'
import { useEvent } from 'react-use-event-hook'
import { createPrivyNotAuthenticatedNotification } from '@components/Notifications/utils'
import { prepareGatedDataForSubmit } from '@components/Tokens/utils'
import { FancyButton } from '@ui'
import { GetSigner, WalletReady } from 'privy/WalletReady'
import { usePanelActions } from 'routes/layouts/hooks/usePanelActions'
import {
    findFixedPricingModule,
    TransactionStatus,
    useEditSpaceMembershipTransaction,
    usePricingModules,
    useRoleDetails,
} from 'use-towns-client'
import { parseUnits } from 'viem'

import { EditMembershipSchemaUsdcType } from './editMembershipSchemaUsdc'
import { checkIsDisabled, checkIsUnchanged } from './editMembershipSubmitHelpers'

/**
 * Rounds a decimal string to a specified number of decimal places.
 * Used to ensure USDC amounts don't exceed 6 decimal places.
 */
function roundToDecimals(value: string, decimals: number): string {
    const num = parseFloat(value)
    if (isNaN(num)) {
        return value
    }
    return num.toFixed(decimals)
}

export function EditMembershipSubmitButtonUsdc({
    children,
    spaceId,
    transactionIsPending,
    pricingModules,
    isLoadingPricingModules,
    isLoadingRoleDetails,
    roleDetails,
    onSuccess,
}: {
    children?: string
    spaceId: string | undefined
    transactionIsPending: boolean
    pricingModules: ReturnType<typeof usePricingModules>['data']
    isLoadingPricingModules: boolean
    isLoadingRoleDetails: boolean
    roleDetails: ReturnType<typeof useRoleDetails>['roleDetails']
    onSuccess?: () => void
}) {
    const { handleSubmit, formState, watch, setError } =
        useFormContext<EditMembershipSchemaUsdcType>()
    const { defaultValues } = formState
    const watchAllFields = watch()
    const { editSpaceMembershipTransaction } = useEditSpaceMembershipTransaction()
    const { closePanel } = usePanelActions()

    const isUnchanged = useMemo(
        () => checkIsUnchanged(defaultValues, watchAllFields),
        [defaultValues, watchAllFields],
    )

    const isDisabled = checkIsDisabled({
        isSubmitting: formState.isSubmitting,
        isUnchanged,
        hasErrors: Object.keys(formState.errors).length > 0,
        gatingType: watchAllFields.gatingType,
        tokensGatedBy: watchAllFields.tokensGatedBy,
        usersGatedBy: watchAllFields.usersGatedBy,
        ethBalanceGatedBy: watchAllFields.ethBalanceGatedBy,
        isLoadingPricingModules,
        isLoadingRoleDetails,
        transactionIsPending,
    })

    const isSubmittedRef = React.useRef(false)

    const onValid = useEvent(async (data: EditMembershipSchemaUsdcType, getSigner: GetSigner) => {
        if (
            !spaceId ||
            !roleDetails ||
            transactionIsPending ||
            isLoadingPricingModules ||
            isSubmittedRef.current
        ) {
            return
        }

        //////////////////////////////////////////
        // check pricing
        //////////////////////////////////////////
        let priceInSmallestUnit: bigint

        try {
            // USDC uses 6 decimals
            const roundedAmount = roundToDecimals(data.membershipCostUsdc, 6)
            priceInSmallestUnit = parseUnits(roundedAmount, 6)
        } catch (error) {
            setError('membershipCostUsdc', {
                type: 'manual',
                message: 'Please enter a valid USDC amount.',
            })
            return
        }

        const setPricingModuleError = () => {
            setError('usdcPricingOption', {
                type: 'manual',
                message: 'Unable to get pricing modules. Please try again later',
            })
        }

        if (!pricingModules) {
            console.warn('No pricing modules found')
            setPricingModuleError()
            return
        }

        const fixedPricingModule = findFixedPricingModule(pricingModules)

        if (!fixedPricingModule) {
            console.warn('Cannot find fixed pricing module')
            setPricingModuleError()
            return
        }

        const pricingModuleToSubmit = await fixedPricingModule.module

        const { usersGatedBy, ruleData } = prepareGatedDataForSubmit(
            data.gatingType,
            data.tokensGatedBy,
            data.usersGatedBy,
            data.ethBalanceGatedBy,
        )

        isSubmittedRef.current = true

        const signer = await getSigner()

        if (!signer) {
            createPrivyNotAuthenticatedNotification()
            isSubmittedRef.current = false
            return
        }

        const txContext = await editSpaceMembershipTransaction({
            spaceId,
            updateRoleParams: {
                roleId: 1,
                roleName: roleDetails.name,
                permissions: roleDetails.permissions,
                spaceNetworkId: spaceId,
                users: usersGatedBy,
                ruleData,
            },
            membershipParams: {
                membershipPrice: priceInSmallestUnit,
                membershipSupply: data.membershipLimit,
                pricingModule: pricingModuleToSubmit,
            },
            signer,
        })

        isSubmittedRef.current = false

        if (txContext?.status === TransactionStatus.Success) {
            closePanel()
            onSuccess?.()
        }
    })

    const onInvalid: SubmitErrorHandler<EditMembershipSchemaUsdcType> = useEvent((errors) => {
        console.error(errors)
    })

    return (
        <WalletReady>
            {({ getSigner }) => (
                <FancyButton
                    cta
                    data-testid="submit-button"
                    disabled={isDisabled}
                    style={{
                        pointerEvents: isDisabled ? 'none' : 'initial',
                    }}
                    onClick={handleSubmit((data) => onValid(data, getSigner), onInvalid)}
                >
                    {children}
                </FancyButton>
            )}
        </WalletReady>
    )
}
